import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle2, Info } from 'lucide-react';
import { HabitDetail } from '../constants';
import { useTranslation } from 'react-i18next';

interface HabitDetailModalProps {
  habit: HabitDetail | null;
  onClose: () => void;
}

export function HabitDetailModal({ habit, onClose }: HabitDetailModalProps) {
  const { t } = useTranslation();

  if (!habit) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
        />

        {/* Modal Content */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="relative w-full max-w-lg bg-white rounded-t-[3rem] sm:rounded-[3rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="p-8 pb-4 flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">{habit.category}</span>
              <h2 className="text-3xl font-extrabold tracking-tighter text-stone-900">{habit.title}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-stone-100 rounded-full text-stone-500 hover:text-stone-900 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-8 pt-4 space-y-8">
            <section>
              <p className="text-lg font-medium text-stone-600 leading-relaxed">
                {habit.description}
              </p>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-bold text-stone-400 uppercase tracking-widest">{t('habits.recommendedActions')}</h3>
              <div className="space-y-3">
                {habit.actions.map((action, index) => (
                  <div key={index} className="flex gap-4 items-start bg-stone-50 p-4 rounded-2xl border border-stone-100">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="font-bold text-stone-700 leading-tight">{action}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-emerald-50 p-6 rounded-[2rem] border border-emerald-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-[0.07]">
                <Info className="w-8 h-8 text-emerald-900" />
              </div>
              <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-widest mb-2">{t('habits.expertOpinion')}</h3>
              <p className="text-emerald-900/80 font-semibold leading-relaxed relative z-10">
                {habit.scientificInsight}
              </p>
            </section>
          </div>

          {/* Footer Action */}
          <div className="p-8 pt-4 bg-white border-t border-stone-100">
            <button
              onClick={onClose}
              className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg shadow-xl hover:bg-stone-800 transition-colors"
            >
              {t('habits.understood')}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
